import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CONTACT_INFO, SHOW_PHONE_CONTACT } from "@/lib/constants";

export interface SiteSettingsRow {
  id: string;
  email: string | null;
  phone_display: string | null;
  phone_raw: string | null;
  whatsapp_number: string | null;
  location: string | null;
  show_phone_contact: boolean;
  show_testimonials: boolean;
  instagram_url: string | null;
  snapchat_url: string | null;
  tiktok_url: string | null;
  youtube_url: string | null;
  facebook_url: string | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  site_name: string | null;
  site_tagline: string | null;
  brand_primary: string | null;
  brand_accent: string | null;
  brand_background: string | null;
  brand_foreground: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
}

export interface ResolvedSiteSettings {
  email: string;
  phone: string;
  phoneRaw: string;
  whatsappLink: string;
  location: string;
  showPhone: boolean;
  showTestimonials: boolean;
  logoUrl: string | null;
  faviconUrl: string | null;
  siteName: string;
  siteTagline: string;
  brand: {
    primary: string | null;
    accent: string | null;
    background: string | null;
    foreground: string | null;
  };
  seoTitle: string | null;
  seoDescription: string | null;
  ogImageUrl: string | null;
  socials: {
    instagram: string | null;
    snapchat: string | null;
    tiktok: string | null;
    youtube: string | null;
    facebook: string | null;
    linkedin: string | null;
    twitter: string | null;
  };
}

const clean = (value?: string | null) => {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
};

export function resolveSiteSettings(row?: SiteSettingsRow | null): ResolvedSiteSettings {
  const whatsappNumber = clean(row?.whatsapp_number)?.replace(/[^0-9]/g, "");
  return {
    email: clean(row?.email) ?? CONTACT_INFO.email,
    phone: clean(row?.phone_display) ?? CONTACT_INFO.phone,
    phoneRaw: clean(row?.phone_raw) ?? CONTACT_INFO.phoneRaw,
    whatsappLink: whatsappNumber ? `https://wa.me/${whatsappNumber}` : CONTACT_INFO.whatsappLink,
    location: clean(row?.location) ?? CONTACT_INFO.location,
    showPhone: row ? row.show_phone_contact : SHOW_PHONE_CONTACT,
    showTestimonials: row ? row.show_testimonials !== false : true,
    logoUrl: clean(row?.logo_url),
    faviconUrl: clean(row?.favicon_url),
    siteName: clean(row?.site_name) ?? "Himilo Studio",
    siteTagline: clean(row?.site_tagline) ?? "",
    brand: {
      primary: clean(row?.brand_primary),
      accent: clean(row?.brand_accent),
      background: clean(row?.brand_background),
      foreground: clean(row?.brand_foreground),
    },
    seoTitle: clean(row?.seo_title),
    seoDescription: clean(row?.seo_description),
    ogImageUrl: clean(row?.og_image_url),
    socials: {
      instagram: row ? clean(row.instagram_url) : CONTACT_INFO.socials.instagram,
      snapchat: row ? clean(row.snapchat_url) : CONTACT_INFO.socials.snapchat,
      tiktok: row ? clean(row.tiktok_url) : CONTACT_INFO.socials.tiktok,
      youtube: row ? clean(row.youtube_url) : CONTACT_INFO.socials.youtube,
      facebook: row ? clean(row.facebook_url) : null,
      linkedin: row ? clean(row.linkedin_url) : null,
      twitter: row ? clean(row.twitter_url) : null,
    },
  };
}

export function useSiteSettings() {
  const query = useQuery({
    queryKey: ["cms-site-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cms_site_settings")
        .select("*")
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return (data as SiteSettingsRow | null) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    row: query.data ?? null,
    settings: resolveSiteSettings(query.data),
  };
}
